import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { manageDisableScrolling } from '../../ducks/ui.duck';

// The widget is published as an ES module and is loaded straight from the CDN at runtime.
// webpackIgnore keeps the bundler from trying to resolve the URL at build time.
// The n8n "Chat Trigger" webhook the conversation is posted to.
// Note: changing this host means updating the CSP whitelist in server/csp.js to match.
const CHAT_WEBHOOK_URL =
  'https://martinappz.app.n8n.cloud/webhook/5682592f-cb20-443e-9ea6-21f1e1c8a8d3/chat';

// Pinned on purpose. An unversioned jsDelivr path resolves to whatever was published most
// recently, so a release upstream would reach users with no commit here - and the selectors this
// integration styles (.chat-window, .chat-inputs, .chat-window-toggle) are not version-negotiated.
const CHAT_VERSION = '1.34.4';
const CHAT_BUNDLE_URL = `https://cdn.jsdelivr.net/npm/@n8n/chat@${CHAT_VERSION}/dist/chat.bundle.es.js`;
const CHAT_STYLESHEET_URL = `https://cdn.jsdelivr.net/npm/@n8n/chat@${CHAT_VERSION}/dist/style.css`;
const CHAT_STYLESHEET_ID = 'n8n-chat-styles';

// Set on <body> while the chat window is actually open, so the mobile styles can turn the
// launcher into a close button. The widget doesn't expose an open/closed state, hence the
// observer below.
const OPEN_BODY_CLASS = 'n8n-chat-open';

const loadStylesheet = () => {
  if (document.getElementById(CHAT_STYLESHEET_ID)) {
    return;
  }
  const link = document.createElement('link');
  link.id = CHAT_STYLESHEET_ID;
  link.rel = 'stylesheet';
  link.href = CHAT_STYLESHEET_URL;
  document.head.appendChild(link);
};

/**
 * Watch whether the chat window is open and report every change.
 *
 * The window element stays in the document whether the chat is open or not - the widget toggles
 * an inline `display: none` on it. So its computed display is what "open" means here; its
 * presence in the DOM tells you nothing.
 *
 * @param {Function} onOpenChange - called with the current open state, including once initially
 * @returns {Function} stops watching
 */
const watchChatWindow = onOpenChange => {
  let frame = null;
  let observer = null;
  let cancelled = false;

  const start = () => {
    if (cancelled) {
      return;
    }
    const chatWindow = document.querySelector('.chat-window');
    if (!chatWindow) {
      frame = requestAnimationFrame(start);
      return;
    }

    const sync = () => {
      onOpenChange(getComputedStyle(chatWindow).display !== 'none');
    };
    sync();

    observer = new MutationObserver(sync);
    observer.observe(chatWindow, { attributes: true, attributeFilter: ['style', 'class'] });
  };

  start();

  return () => {
    cancelled = true;
    if (frame) {
      cancelAnimationFrame(frame);
    }
    if (observer) {
      observer.disconnect();
    }
  };
};

/**
 * Keep a CSS variable in step with the visual viewport height.
 *
 * On phones the on-screen keyboard shrinks the visible area but leaves 100dvh unchanged, so a
 * full-height chat window keeps its input and send button underneath the keyboard, with the page
 * showing through where the window no longer reaches. Both the height and the top offset are
 * tracked, since iOS also scrolls the visual viewport within the layout viewport.
 *
 * @returns {Function} stops tracking
 */
const trackViewportHeight = () => {
  const viewport = window.visualViewport;
  if (!viewport) {
    return () => {};
  }

  const sync = () => {
    const root = document.documentElement.style;
    root.setProperty('--n8n-chat-viewport-height', `${viewport.height}px`);
    // Height alone is not enough. When the keyboard opens, iOS scrolls the visual viewport down
    // inside the layout viewport, and a position:fixed element stays with the layout viewport -
    // so the chat would sit above the visible strip. offsetTop puts it back.
    root.setProperty('--n8n-chat-viewport-top', `${viewport.offsetTop}px`);
  };
  sync();

  viewport.addEventListener('resize', sync);
  viewport.addEventListener('scroll', sync);

  return () => {
    viewport.removeEventListener('resize', sync);
    viewport.removeEventListener('scroll', sync);
    document.documentElement.style.removeProperty('--n8n-chat-viewport-height');
    document.documentElement.style.removeProperty('--n8n-chat-viewport-top');
  };
};

/**
 * Support chat, shown in the bottom corner on every page.
 *
 * Renders nothing itself: the widget injects its own DOM. It is only mounted client-side, since
 * the bundle is fetched from a CDN at runtime.
 *
 * Note: the CDN and the webhook host both have to be whitelisted in server/csp.js. The look of
 * the widget is set in src/styles/n8nChat.css - those rules target the widget's own class names,
 * so they have to be global rather than a CSS module.
 *
 * @component
 * @returns {null}
 */
const ChatWidget = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let stopWatching = null;
    let stopTrackingViewport = null;
    let cancelled = false;

    // On phones the open chat covers the whole screen, so the page behind it is frozen through
    // the app's own scroll lock (ui.duck -> Page renders position: fixed). This is what keeps iOS
    // from panning the page when the keyboard opens - without it the page slides around behind
    // the chat and shows through next to the keyboard. Desktop keeps scrolling: there the chat is
    // a corner window.
    // matchMedia is missing in JSDOM; without it, behave as desktop (no scroll lock).
    const mobileQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(max-width: 768px)')
        : { matches: false };
    let isOpen = false;

    const applyScrollLock = () => {
      dispatch(manageDisableScrolling('ChatWidget', isOpen && mobileQuery.matches));
    };

    const handleOpenChange = nextOpen => {
      isOpen = nextOpen;
      document.body.classList.toggle(OPEN_BODY_CLASS, nextOpen);
      applyScrollLock();
    };

    // Re-evaluate when the viewport crosses the mobile boundary while the chat is open.
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', applyScrollLock);
    }

    loadStylesheet();

    import(/* webpackIgnore: true */ CHAT_BUNDLE_URL)
      .then(({ createChat }) => {
        if (cancelled) {
          return;
        }
        createChat({
          webhookUrl: CHAT_WEBHOOK_URL,
          mode: 'window',
          chatInputKey: 'chatInput',
          chatSessionKey: 'sessionId',
          loadPreviousSession: true,
          showWelcomeScreen: false,
          defaultLanguage: 'en',
          initialMessages: ['Hi there! 👋', 'How can I help you today?'],
          i18n: {
            en: {
              title: "Hi! I'm Nettie. 👋",
              subtitle: 'Your AI Chat Assistant. How can I help?',
              footer: '',
              getStarted: 'New Conversation',
              inputPlaceholder: 'Type your question..',
            },
          },
          enableStreaming: false,
        });

        stopWatching = watchChatWindow(handleOpenChange);
        stopTrackingViewport = trackViewportHeight();
      })
      .catch(e => {
        // The chat is a nice-to-have: a CDN hiccup shouldn't take anything else down.
        console.error('Failed to load the chat widget', e); // eslint-disable-line no-console
      });

    return () => {
      cancelled = true;
      if (stopWatching) {
        stopWatching();
      }
      if (stopTrackingViewport) {
        stopTrackingViewport();
      }
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', applyScrollLock);
      }
      dispatch(manageDisableScrolling('ChatWidget', false));
      document.body.classList.remove(OPEN_BODY_CLASS);
    };
  }, [dispatch]);

  return null;
};

export default ChatWidget;
